import { useEffect, useState, useRef } from "react";
import { Plus, Search, X } from "lucide-react";
import { MetricsCards } from "../../components/supplierDashboard/MetricsCards";
import { SupplierTable } from "../../components/supplierDashboard/SupplierTable";
import { AddSupplierDialog } from "../../components/supplierDashboard/AddSupplierDialog";
import { SupplierProfile } from "../../components/supplier/SupplierProfile";
import { mockPurchaseOrders, mockInvoices, mockAssets } from "../../data/mockData";
import { DashboardMetrics, Supplier } from "../../types/supplier";
import apiService from "../../services/apiService";
import { RMList } from "../../utils/interfaces";
import { debounce } from "lodash";

const emptyDashboardMetrics: DashboardMetrics = {
  totalSuppliers: 0,
  totalPOs: 0,
  invoiceValue: { billed: 0, paid: 0, pending: 0 },
  assetsCount: 0,
};

interface DesignationResponse {
  data: DesignationTypes[];
}

interface DesignationTypes {
  id: number;
  name: string;
  display_order: number;
}

interface RMListResponse {
  data: RMList[];
}

interface pagination {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface RequirementResponse {
  total_suppliers?: number;
  suppliers: any[];
  pagination: pagination;
  total_assets?: number;
  total_proforma_invoices?: number;
  total_proforma_invoices_values?: number;
  total_paid_proforma_invoices_values?: number;
  total_pending_proforma_invoice_values?: number;
}

const ManageSupplier = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [designationList, setDesignationList] = useState<DesignationTypes[]>([]);
  const [indiaState, setIndiaState] = useState<any[]>([]);
  const [uniqueRmNames, setUniqueRmNames] = useState<RMList[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyTypes, setCompanyTypes] = useState<any[]>([]);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics>(emptyDashboardMetrics);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const delayedFetch = debounce(() => {
      fetchSuppliers(searchTerm, currentPage, pageSize);
    }, 300);

    delayedFetch();

    return () => delayedFetch.cancel();
  }, [searchTerm, pageSize, currentPage]);

  useEffect(() => {
    fetchStates();
    fetchLenderDesignation();
    getRMListData();
    fetchCompanyTypes();
  }, []);

  const getRMListData = async () => {
    try {
      const response: RMListResponse = await apiService.get("/users/rm-list");
      setUniqueRmNames(response.data);
    } catch (e) {
      // fallback silent
    }
  };

  const fetchLenderDesignation = async () => {
    try {
      const response = await apiService.get<DesignationResponse>(`/lender/get-lender-designation-types`);
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id,
        display_order: doc.display_order,
      }));
      setDesignationList(transformedDocs);
    } catch (error) {
      console.error("Error fetching Partner types:", error);
    }
  };

  const fetchSuppliers = async (f = searchTerm, page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const response = await apiService.post<RequirementResponse>(
        `/supplier/getSuppliers`,
        { search: f, pagination: { pageSize: size, currentPage: page } }
      );
      setSuppliers(response?.suppliers || []);
      setTotal(response?.pagination?.totalRecords || 0);
      if (response?.pagination?.currentPage) {
        setCurrentPage(response.pagination.currentPage);
      }
      setDashboardMetrics({
        totalSuppliers: Number(response?.total_suppliers) || response?.suppliers?.length || 0,
        totalPOs: Number(response?.total_proforma_invoices) || 0,
        invoiceValue: {
          billed: Number(response?.total_proforma_invoices_values) || 0,
          paid: Number(response?.total_paid_proforma_invoices_values) || 0,
          pending: Number(response?.total_pending_proforma_invoice_values) || 0,
        },
        assetsCount: Number(response?.total_assets) || 0,
      });
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setSuppliers([]);
      setDashboardMetrics(emptyDashboardMetrics);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const fetchStates = async () => {
    try {
      const response = await apiService.get<any>(`/supplier/get-india-state`);
      setIndiaState(response.data);
    } catch (e) {
      // fallback
    }
  };

  const fetchCompanyTypes = async () => {
    try {
      const response = await apiService.get<any>(`/supplier/company-types`);
      setCompanyTypes(response.data);
    } catch (e) {
      // fallback
    }
  };

  const handleEditSupplier = (updatedSupplier: Supplier) => {
    setSuppliers((prev) =>
      prev.map((supplier) =>
        String(supplier.id) === String(updatedSupplier.id) ? updatedSupplier : supplier
      )
    );
    setSelectedSupplier(updatedSupplier);
  };

  const handleSupplierAdded = () => {
    fetchSuppliers(searchTerm, currentPage, pageSize);
  };

  const handleBackToDashboard = () => {
    setSelectedSupplier(null);
  };

  if (selectedSupplier) {
    return (
      <div className="min-h-screen w-full flex-1 bg-[#ffffff] text-[#151b1d] font-['Poppins',sans-serif]" style={{ width: "100%", minWidth: "100%" }}>
        <div className="w-full px-5 py-5 sm:px-6 lg:px-8">
          <SupplierProfile
            companyTypes={companyTypes}
            supplier={selectedSupplier}
            onBack={handleBackToDashboard}
            onEdit={handleEditSupplier}
            purchaseOrders={mockPurchaseOrders}
            invoices={mockInvoices}
            assets={mockAssets}
            indianStates={indiaState || []}
            designationList={designationList}
            uniqueRmNames={uniqueRmNames}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ffffff] w-full flex-1 text-[#151b1d] font-['Poppins',sans-serif]" style={{ width: "100%", minWidth: "100%" }}>
      <div className="w-full px-5 py-5 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          {/* Left Title & Subtitle */}
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-[22px] font-semibold text-[#151b1d] tracking-tight">
                Suppliers
              </h1>
              <span className="bg-[#e1f0f1] text-[#2e6b76] text-xs font-semibold px-2 py-0.5 rounded-full">
                {total || suppliers.length || 0}
              </span>
            </div>
            <p className="text-[13px] text-[#7c8b91] font-normal mt-1">
              Vendors onboarded for leased assets, their contacts, assets and invoices
            </p>
          </div>

          {/* Right Actions: Search & Add Supplier */}
          <div className="flex items-center gap-3">
            {/* Search Input */}
            <div className="relative w-full sm:w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7c8b91]" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by supplier, PAN, GSTIN or co"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-8 rounded-[8px] border border-[#dde3e4] bg-white text-[13px] text-[#151b1d] placeholder:text-[#7c8b91] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#7c8b91] hover:text-[#151b1d]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Add Supplier Button */}
            <button
              type="button"
              onClick={() => setShowAddDialog(true)}
              className="h-9 px-4 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-medium flex items-center gap-1.5 transition-colors cursor-pointer flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span>Add Supplier</span>
            </button>
          </div>
        </div>

        {/* KPI Metrics Cards */}
        <MetricsCards metrics={dashboardMetrics} />

        {/* Supplier Table */}
        <SupplierTable
          suppliers={suppliers}
          onViewSupplier={handleViewSupplier}
          onEditSupplier={handleEditSupplier}
          total={total}
          currentPage={currentPage}
          pageSize={pageSize}
          loading={loading}
          onPageChange={onPageChange}
          companyTypes={companyTypes}
        />

        {/* Add Supplier Modal */}
        <AddSupplierDialog
          open={showAddDialog}
          companyTypes={companyTypes}
          onOpenChange={setShowAddDialog}
          onSupplierAdded={handleSupplierAdded}
        />
      </div>
    </div>
  );
};

export default ManageSupplier;
